import { KeyRound, Menu, PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, UnauthorizedError } from "./api";
import { AccessManager } from "./components/AccessManager";
import { Article, ArticleError, ArticleSkeleton } from "./components/Article";
import { AuthScreen } from "./components/AuthScreen";
import { SearchDialog } from "./components/SearchDialog";
import { Sidebar } from "./components/Sidebar";
import { DesktopToc, MobileToc } from "./components/TableOfContents";
import { ThemeToggle } from "./components/ThemeToggle";
import { useMedia } from "./hooks/useMedia";
import { useTheme } from "./hooks/useTheme";
import type { Bootstrap, WikiPage } from "./types";

function pathFromLocation(): string {
  if (location.pathname === "/" || !location.pathname) return "index.md";
  try {
    return location.pathname.slice(1).split("/").map(decodeURIComponent).join("/");
  } catch {
    return location.pathname.slice(1);
  }
}

function urlForPath(path: string, hash = ""): string {
  if (path === "index.md") return `/${hash}`;
  return `/${path.split("/").map(encodeURIComponent).join("/")}${hash}`;
}

export default function App() {
  const mobile = useMedia("(max-width: 800px)");
  const compact = useMedia("(max-width: 1120px)");
  const { preference, setPreference } = useTheme();
  const [authState, setAuthState] = useState<{ checked: boolean; configured: boolean; authenticated: boolean; passkeysConfigured: boolean }>({ checked: false, configured: false, authenticated: false, passkeysConfigured: false });
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [currentPath, setCurrentPath] = useState(pathFromLocation);
  const [page, setPage] = useState<WikiPage | null>(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [restoringScroll, setRestoringScroll] = useState(false);

  const handleUnauthorized = useCallback(() => {
    setAuthState((state) => ({ ...state, authenticated: false, checked: true }));
    setBootstrap(null);
    setPage(null);
  }, []);

  const loadBootstrap = useCallback(async () => {
    try {
      const data = await api.bootstrap();
      setBootstrap(data);
    } catch (error) {
      if (error instanceof UnauthorizedError) handleUnauthorized();
      else throw error;
    }
  }, [handleUnauthorized]);

  const loadPage = useCallback(async (path: string) => {
    setLoading(true);
    setPageError("");
    try {
      const data = await api.page(path);
      setPage(data);
      document.title = `${data.title} · Yufo Knowledge`;
    } catch (error) {
      if (error instanceof UnauthorizedError) handleUnauthorized();
      else setPageError(error instanceof Error ? error.message : "无法读取页面。");
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    void api.authStatus().then((status) => setAuthState({ ...status, checked: true })).catch(() => setAuthState({ checked: true, configured: true, authenticated: false, passkeysConfigured: false }));
  }, []);

  useEffect(() => {
    if (!authState.authenticated) return;
    void loadBootstrap();
    void loadPage(currentPath);
  }, [authState.authenticated, currentPath, loadBootstrap, loadPage]);

  useEffect(() => {
    if (!authState.authenticated) return;
    const events = new EventSource("/api/events");
    const update = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as { head?: string; changedPaths?: string[]; sourceState?: Bootstrap["sourceState"] };
      setBootstrap((previous) => previous ? { ...previous, head: data.head ?? previous.head, headShort: (data.head ?? previous.head).slice(0, 8), sourceState: data.sourceState ?? previous.sourceState } : previous);
      if (data.changedPaths?.includes(currentPath)) void loadPage(currentPath);
      void loadBootstrap();
    };
    events.addEventListener("snapshot", update as EventListener);
    events.addEventListener("source-state", update as EventListener);
    events.onerror = () => setBootstrap((previous) => previous ? { ...previous, sourceState: "error" } : previous);
    return () => events.close();
  }, [authState.authenticated, currentPath, loadBootstrap, loadPage]);

  useEffect(() => {
    const pop = () => {
      setRestoringScroll(true);
      setCurrentPath(pathFromLocation());
    };
    addEventListener("popstate", pop);
    return () => removeEventListener("popstate", pop);
  }, []);

  useEffect(() => {
    if (!page || page.path !== currentPath) return;
    requestAnimationFrame(() => {
      if (location.hash) document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
      else if (restoringScroll) window.scrollTo({ top: Number(sessionStorage.getItem(`scroll:${currentPath}`) ?? 0) });
      else window.scrollTo({ top: 0 });
      setRestoringScroll(false);
    });
  }, [page, currentPath, restoringScroll]);

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    addEventListener("keydown", shortcut);
    return () => removeEventListener("keydown", shortcut);
  }, []);

  useEffect(() => {
    if (mobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [mobile]);

  const navigate = useCallback((path: string, hash = "") => {
    sessionStorage.setItem(`scroll:${currentPath}`, String(window.scrollY));
    if (path === currentPath && hash) {
      history.pushState({}, "", urlForPath(path, hash));
      document.getElementById(decodeURIComponent(hash.replace(/^#/, "")))?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    history.pushState({}, "", urlForPath(path, hash));
    setRestoringScroll(false);
    setCurrentPath(path);
  }, [currentPath]);

  const sourceState = bootstrap?.sourceState ?? page?.sourceState ?? "updating";
  const pathSegments = useMemo(() => currentPath.split("/"), [currentPath]);

  if (!authState.checked) return <div className="boot-screen"><span className="live-dot" /> 正在连接实时源</div>;
  if (!authState.authenticated) {
    return <AuthScreen configured={authState.configured} passkeysConfigured={authState.passkeysConfigured} onAuthenticated={() => {
      setAuthState((state) => ({ ...state, authenticated: true }));
    }} onLogin={async (password) => {
      await api.login(password);
      setAuthState((state) => ({ ...state, authenticated: true }));
    }} />;
  }

  return (
    <div className="reader-app" data-sidebar={sidebarOpen && !mobile ? "open" : "closed"}>
      <header className="app-header">
        <div className="header-left">
          <button type="button" className="icon-button menu-button" onClick={() => setSidebarOpen((value) => !value)} aria-label={sidebarOpen ? "收起目录" : "打开目录"}>
            {mobile ? <Menu size={20} /> : sidebarOpen ? <PanelLeftClose size={19} /> : <PanelLeftOpen size={19} />}
          </button>
          <button type="button" className="brand" onClick={() => navigate("index.md")} aria-label="返回知识库首页">
            <span>YK</span><strong>Yufo Knowledge</strong>
          </button>
        </div>
        <div className="header-path" title={currentPath}>
          {pathSegments.map((part, index) => <span key={`${part}-${index}`}>{index > 0 && <i>/</i>}{part}</span>)}
        </div>
        <div className="header-actions">
          <div className="header-source" data-state={sourceState}><span className="live-dot" /><b>{sourceState === "live" ? "LIVE" : sourceState === "updating" ? "SYNC" : "STALE"}</b>{!compact && <code>{bootstrap?.headShort ?? "--------"}</code>}</div>
          <button type="button" className="search-button" onClick={() => setSearchOpen(true)}><Search size={17} /><span>搜索</span><kbd>⌘K</kbd></button>
          <button type="button" className="icon-button access-button" title="访问管理" onClick={() => { setSearchOpen(false); setAccessOpen(true); }} aria-label="管理通行密钥"><KeyRound size={18} /></button>
          <ThemeToggle value={preference} onChange={setPreference} />
        </div>
      </header>

      {mobile && page && <MobileToc toc={page.toc} />}

      {bootstrap && (
        <Sidebar
          tree={bootstrap.tree}
          currentPath={currentPath}
          open={sidebarOpen}
          mobile={mobile}
          onClose={() => setSidebarOpen(false)}
          onNavigate={navigate}
          onSearch={() => setSearchOpen(true)}
          onLogout={async () => { await api.logout(); handleUnauthorized(); }}
        />
      )}

      <main className="reader-main" id="main-content">
        <div className="article-column">
          {loading && !page ? <ArticleSkeleton /> : pageError ? <ArticleError message={pageError} onRetry={() => void loadPage(currentPath)} /> : page ? <Article page={page} sourceState={sourceState} onNavigate={navigate} /> : <ArticleSkeleton />}
        </div>
        {!compact && page && <DesktopToc toc={page.toc} />}
      </main>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={navigate} />
      <AccessManager open={accessOpen} onClose={() => setAccessOpen(false)} />
    </div>
  );
}
